export function createProductEventCollector(send: (name: string, payload: Record<string, unknown>) => void) {
  const seen = new Set<string>();
  let sequence = 0;
  const session = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
  const id = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? String(value) : text(value);
  const position = (value: unknown) => typeof value === "number" && Number.isInteger(value) && value >= 0;

  return (name: ProductEventName, input: ProductEventInput): boolean => {
    try {
      if (!["select_item", "view_item", "select_product_variant"].includes(name) || !input) return false;
      const productId = id(input.product?.id);
      const title = text(input.product?.title);
      const placement = text(input.placement);
      if (!productId || !title || !placement) return false;
      if (name === "select_item" && (!text(input.listId) || !text(input.listName) || !position(input.index))) return false;
      if (name === "view_item" && !text(input.presentationId)) return false;
      if (name === "select_product_variant" && (!text(input.optionName) || !text(input.optionValue) || input.previousValue === input.optionValue)) return false;

      const eventId = text(input.eventId) || `theme-${session}-${++sequence}`;
      const key = name === "view_item" ? `${name}:${input.presentationId}` : `${name}:${eventId}`;
      if (seen.has(key)) return false;
      const item: Record<string, unknown> = { item_id: productId, item_name: title, product_id: productId };
      const variantId = id(input.variant?.id);
      if (variantId) {
        item.variant_id = variantId;
        if (text(input.variant?.title)) item.item_variant = input.variant.title;
        if (text(input.variant?.sku)) item.sku = input.variant.sku;
      }
      if (text(input.listId)) item.item_list_id = input.listId;
      if (text(input.listName)) item.item_list_name = input.listName;
      if (position(input.index)) item.index = input.index;
      if (position(input.kitPosition)) item.kit_position = input.kitPosition;

      const payload: Record<string, unknown> = {
        event_id: eventId, event_source: "theme_product_events", event_schema_version: 1,
        placement, origin: text(input.origin) || "unknown", origin_placement: text(input.originPlacement) || "unknown",
        origin_list_id: text(input.originListId) || null, origin_list_name: text(input.originListName) || null,
        origin_index: position(input.originIndex) ? input.originIndex : null,
        ecommerce: { items: [item] },
        presentation_id: null, option_name: null, option_value: null, previous_option_value: null,
        selection_complete: null, selected_options: null, experiment_id: null, experiment_group: null,
      };
      if (text(input.presentationId)) payload.presentation_id = input.presentationId;
      if (name === "select_product_variant") {
        payload.option_name = input.optionName;
        payload.option_value = input.optionValue;
        payload.previous_option_value = input.previousValue ?? null;
        payload.selection_complete = Boolean(variantId);
      }
      if (Array.isArray(input.selectedOptions)) payload.selected_options = input.selectedOptions.map(v => text(v) || null);
      if (text(input.experiment?.id) && text(input.experiment?.group)) {
        payload.experiment_id = input.experiment.id;
        payload.experiment_group = input.experiment.group;
      }
      send(name, payload);
      seen.add(key);
      if (seen.size > 200) seen.delete(seen.values().next().value);
      return true;
    } catch {
      return false;
    }
  };
}
